import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Calendar, Package, Video, Clock, MapPin, ArrowRight, Play, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const [bookingsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/bookings`, { withCredentials: true }),
          axios.get(`${API}/orders`, { withCredentials: true })
        ]);
        setBookings(bookingsRes.data);
        setOrders(ordersRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    paid: 'bg-green-100 text-green-700',
    shipped: 'bg-purple-100 text-purple-700',
    delivered: 'bg-green-100 text-green-700'
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#FAFBFC] pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-[#1A202C]">
              Namaste, {user?.name}
            </h1>
            <p className="text-[#718096]">Welcome to your spiritual dashboard</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Bookings', value: bookings.length, icon: Calendar },
              { label: 'Completed Pujas', value: bookings.filter(b => b.status === 'completed').length, icon: Video },
              { label: 'Total Orders', value: orders.length, icon: Package },
              { label: 'Pending Deliveries', value: orders.filter(o => o.status === 'shipped').length, icon: Truck },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"
              >
                <stat.icon className="w-8 h-8 text-[#FF8C00] mb-2" />
                <p className="text-2xl font-bold text-[#1A202C]">{stat.value}</p>
                <p className="text-sm text-[#718096]">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Tabs */}
          <Tabs defaultValue="rituals" className="space-y-6">
            <TabsList className="bg-white rounded-full p-1 shadow-sm">
              <TabsTrigger 
                value="rituals" 
                className="rounded-full data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white"
                data-testid="tab-rituals"
              >
                My Rituals
              </TabsTrigger>
              <TabsTrigger 
                value="orders"
                className="rounded-full data-[state=active]:bg-[#FF8C00] data-[state=active]:text-white"
                data-testid="tab-orders"
              >
                My Orders
              </TabsTrigger>
            </TabsList>

            {/* Rituals Tab */}
            <TabsContent value="rituals" className="space-y-4">
              {bookings.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl">
                  <Calendar className="w-16 h-16 text-[#D1D5DB] mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-[#4A5568] mb-2">
                    No bookings yet
                  </h3>
                  <p className="text-[#718096] mb-4">Start your spiritual journey today</p>
                  <Link to="/temples">
                    <Button className="btn-saffron rounded-full px-6" data-testid="book-first-puja-btn">
                      Book Your First Puja
                    </Button>
                  </Link>
                </div>
              ) : (
                bookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"
                    data-testid={`booking-card-${booking.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Image */}
                      <div className="w-full md:w-32 h-24 rounded-xl overflow-hidden bg-[#FAFBFC]">
                        <img
                          src={booking.puja?.image_url || booking.temple?.image_url}
                          alt={booking.puja?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-heading text-lg font-bold text-[#1A202C]">
                              {booking.puja?.name}
                            </h3>
                            <p className="text-sm text-[#718096]">{booking.temple?.name}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[booking.status]}`}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#4A5568] mt-1">
                          {booking.package_name && (
                            <span className="font-semibold text-[#6E1B34] bg-[#FBF4E7] px-2 py-0.5 rounded">
                              {booking.package_name}
                            </span>
                          )}
                          {booking.puja_date && (
                            <span className="text-gray-600">
                              📅 {new Date(booking.puja_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          <div className="flex items-center space-x-1">
                            <span><strong>Devotees:</strong> {booking.devotee_names?.join(', ') || 'Devotee'}</span>
                          </div>
                          {booking.gotras?.length > 0 && (
                            <div className="flex items-center space-x-1">
                              <span><strong>Gotra:</strong> {booking.gotras.join(', ')}</span>
                            </div>
                          )}
                          {booking.puja_wish && (
                            <div className="w-full text-[11px] text-gray-500 italic mt-0.5">
                              Sankalp Wish: "{booking.puja_wish}"
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {booking.video_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full border-[#FF8C00] text-[#FF8C00]"
                            data-testid={`watch-video-${booking.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Watch Video
                          </Button>
                        )}
                        <Link to={`/tracking/${booking.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#4A6274]"
                            data-testid={`track-prasad-${booking.id}`}
                          >
                            Track Prasad
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders" className="space-y-4">
              {orders.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl">
                  <Package className="w-16 h-16 text-[#D1D5DB] mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-[#4A5568] mb-2">
                    No orders yet
                  </h3>
                  <p className="text-[#718096] mb-4">Explore our authentic Himalayan products</p>
                  <Link to="/store">
                    <Button className="btn-saffron rounded-full px-6" data-testid="shop-now-btn">
                      Shop Now
                    </Button>
                  </Link>
                </div>
              ) : (
                orders.map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"
                    data-testid={`order-card-${order.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-sm text-[#718096]">Order #{order.id.slice(0, 8)}</p>
                        <p className="text-xs text-[#718096]">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[order.status]}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-[#4A5568]">{item.name} x{item.quantity}</span>
                          <span className="text-[#1A202C]">₹{item.subtotal?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 flex-wrap gap-3">
                      <div>
                        <span className="text-xs text-gray-500 block">Total Amount</span>
                        <span className="price-tag text-xl font-bold text-gray-900">₹{order.total_amount?.toLocaleString()}</span>
                        {order.is_gift && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full border border-rose-200">
                            🎁 Gift
                          </span>
                        )}
                      </div>
                      <Link to={`/orders/${order.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full border-[#FF7B00] text-[#FF7B00] hover:bg-[#FF7B00] hover:text-white font-bold text-xs"
                          data-testid={`view-order-${order.id}`}
                        >
                          View Details & Invoice &rarr;
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DashboardPage;
